/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import loadable from '@loadable/component';

const Rating = loadable(() => import('../../../components/Rating/Rating'));
const ReviewForm = loadable(() => import('./ReviewForm'));
const Button = loadable(() => import('../../../components/Button/Button'));

import { comparators, getReviewRatings } from './reviewUtils';

import styles from './ReviewsComponent.module.scss';
import ReviewSuccessModal from './ReviewSucessModal';
import { SECONDARY } from '../../../components/Button/Button';
import { ReactComponent as Pug } from '../../../images/icons/pug-no-review.svg';
import { ReactComponent as Verify } from '../../../images/icons/verify.svg';

const ReviewsComponent = ({
  openLoginModal,
  headerRenderer,
  ratingMessageRenderer,
  averageRating = 0,
  ratingCount = 0,
  ratingCountsByScore = undefined,
  comments = undefined,
  addReviewRequest,
  formId,
  classList = {},
  reverseContent = false,
  notShowFilter = false,
  isEmptyState = false,
}) => {
  const intl = useIntl();
  const [filter, setFilter] = useState('recent');
  const [reviewFormState, setReviewFormState] = useState('idle');
  const [showReviewForm, setShowReviewForm] = useState(false);

  const closeReviewForm = () => {
    setReviewFormState('idle');
    setShowReviewForm(false);
  };

  const onSuccessReview = () => {
    setReviewFormState('success');
    setShowReviewForm(false);
  };

  const commentsProvided = !!comments?.length && Array.isArray(comments);

  return (
    <div
      id="ShopReviewsSection"
      className={classNames(reverseContent ? styles.customShopReviews : styles.shopReviews, 'col-12-xs')}
      data-testid="ShopReviewsSection"
    >
      <h2 className={classNames(classList.title, styles.title)} data-testid="ShopReviewsHeader">
        {headerRenderer()}
      </h2>
      {(!isEmptyState || (isEmptyState && showReviewForm)) && (
        <div className={styles.sidebar}>
          <div className={styles.summary}>
            <div className={styles.allReviewsRating}>
              <div className={styles.ratingCircle}>{averageRating}</div>
            </div>
            <div className={styles.totalRatings}>
              <Rating value={averageRating} />
              <p>{ratingMessageRenderer(ratingCount)}</p>
            </div>
          </div>
          {!showReviewForm && (
            <Button
              className={styles.subOrderAction}
              small
              variation={SECONDARY}
              onClick={() => setShowReviewForm(true)}
            >
              <FormattedMessage id="product.review" defaultMessage="Leave a review" />
            </Button>
          )}
          <div className={styles.allRatings}>
            {getReviewRatings(styles, Rating, ratingCountsByScore, ratingCount, comments)}
          </div>
          {showReviewForm && (
            <ReviewForm
              addReviewRequest={addReviewRequest}
              formId={formId}
              openLoginModal={openLoginModal}
              onSuccess={onSuccessReview}
            />
          )}
        </div>
      )}
      <div className={styles.reviewsList}>
        {!commentsProvided && (
          <>
            {isEmptyState && (
              <div className={styles.emptyState}>
                <Pug />
                <div className={styles.emptyStateText}>
                  <span className={styles.emptyStateTitle}>
                    <FormattedMessage
                      id="reviews.product.noReviews"
                      defaultMessage="There are no reviews for this product yet"
                    />
                  </span>
                  <span className={styles.emptyStateSubTitle}>
                    <FormattedMessage
                      id="reviews.product.addReview"
                      defaultMessage="Wanna to be the first to leave a review?"
                    />
                  </span>
                  {!showReviewForm && (
                    <Button
                      className={styles.emptyStateButton}
                      small
                      variation={SECONDARY}
                      onClick={() => setShowReviewForm(true)}
                    >
                      <FormattedMessage id="product.review" defaultMessage="Leave a review" />
                    </Button>
                  )}
                </div>
              </div>
            )}
            {!isEmptyState && (
              <div className={styles.noReviewsContent}>
                <FormattedMessage id="reviews.noReviews" defaultMessage="There are no reviews yet" />
              </div>
            )}
          </>
        )}
        {!!commentsProvided && !notShowFilter && (
          <div className={styles.sortingPills}>
            <div
              className={classNames(styles.pill, { [styles.activePill]: filter === 'recent' })}
              onClick={() => setFilter('recent')}
              data-testid="shopDetailRecent"
            >
              <span>
                <FormattedMessage id="shop.sortBy.recent" defaultMessage="Most" />
              </span>
            </div>
            <div
              className={classNames(styles.pill, { [styles.activePill]: filter === 'highest' })}
              onClick={() => setFilter('highest')}
              data-testid="shopDetailHighest"
            >
              <span>
                <FormattedMessage id="shop.sortBy.highest" defaultMessage="Highest" />
              </span>
            </div>
            <div
              className={classNames(styles.pill, { [styles.activePill]: filter === 'lowest' })}
              onClick={() => setFilter('lowest')}
              data-testid="shopDetailLowest"
            >
              <span>
                <FormattedMessage id="shop.sortBy.lowest" defaultMessage="Lowest" />
              </span>
            </div>
          </div>
        )}
        {!!commentsProvided && (
          <div>
            {comments.sort(comparators[filter]).map((review, index) => {
              return (
                <div key={review + index} className={styles.review}>
                  <header className={styles.header}>
                    <div className={styles.headline}>
                      <h3 className={styles.headlineTitle}>{review.title}</h3>
                      <p className={styles.userLine}>
                        {review?.visitor?.name ||
                          intl.formatMessage({ id: 'reviews.anonymous', defaultMessage: 'Anonymous' })}
                        {!!review?.visitor?.age && `, ${review.visitor.age}`}
                        {review?.visitor?.city && ` | ${review.visitor.city}`}
                      </p>
                      {!!review.verified_purchase && (
                        <div className={styles.verify}>
                          <Verify />
                          <FormattedMessage id="reviews.verified" defaultMessage="Verified purchase" />
                        </div>
                      )}
                    </div>
                    <div>
                      <Rating className={styles.reviewRating} key={`${review.rating + index}`} value={review.rating} />
                      {/* todo: make sure reviews from api are coming in a consistent format. That one from PDP is correct one. On SDP is old one */}
                      <p className={styles.reviewDate}>{review?.visitor?.date || review?.date}</p>
                    </div>
                  </header>
                  <section className={styles.reviewContent}>
                    <p>{review.content || review.text}</p>
                    {!!review?.pros?.length && Array.isArray(review.cons) && (
                      <dl>
                        <dt>
                          <FormattedMessage id="reviews.pros" defaultMessage="Pros" />:
                        </dt>
                        {review.pros.map((pro, proid) => (
                          <dd className={styles.pro} key={`review${index}pro${proid}`}>
                            {pro}
                          </dd>
                        ))}
                      </dl>
                    )}
                    {!!review?.cons?.length && Array.isArray(review.cons) && (
                      <dl>
                        <dt>
                          <FormattedMessage id="reviews.cons" defaultMessage="Pros" />:
                        </dt>
                        {review.cons.map((pro, consid) => (
                          <dd className={styles.cons} key={`review${index}cons${consid}`}>
                            {pro}
                          </dd>
                        ))}
                      </dl>
                    )}
                  </section>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <ReviewSuccessModal isOpen={reviewFormState === 'success'} closeSuccessModal={closeReviewForm} />
    </div>
  );
};

export default ReviewsComponent;
